import React from 'react';
import {
  Checkbox,
  RadioButton,
  CheckboxChangeEvent,
  RadioButtonChangeEvent
} from '@progress/kendo-react-inputs';
import { useParams } from 'react-router-dom';
import { Popup } from '@progress/kendo-react-popup';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { caretAltDownIcon, caretAltUpIcon } from '@progress/kendo-svg-icons';

import { PlayerState } from '../../types/state';
import { FilterKey, FilterState } from '../../pages/facilitator/map-view';
import { useGetPlayerStateByPlayerCode } from '../../hooks/use-game-player-state';

type TaskMode = 'all' | 'available';

interface FilterItem {
  key: FilterKey;
  label: string;
}

const FILTER_ITEMS: FilterItem[] = [
  { key: 'tasks', label: 'Tasks' },
  { key: 'players', label: 'Players' },
  { key: 'offlinePlayers', label: 'Offline Players' }
];

const MapViewFilter: React.FC<{
  filters: FilterState;
  selectedPlayer?: PlayerState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
}> = ({ filters, selectedPlayer, setFilters }) => {
  const { gameCode } = useParams();

  const anchorRef = React.useRef(null);
  const popupRef = React.useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = React.useState<boolean>(false);
  const [taskMode, setTaskMode] = React.useState<TaskMode>('all');

  const { data } = useGetPlayerStateByPlayerCode(
    gameCode,
    selectedPlayer?.code ?? ''
  );

  const unavailableTask = React.useMemo(() => {
    if (!data || !data.playerState) return [];

    return (data.playerState.tasks ?? [])
      .filter(
        (task) =>
          !task.isVisible || (task.status && task.status === 'completed')
      )
      .map((task) => task.id);
  }, [data]);

  const handleDocClick = React.useCallback((e: MouseEvent) => {
    const anchor = (anchorRef.current as any)?.element as HTMLElement | null;
    const popup = popupRef.current;

    if (
      anchor &&
      !anchor.contains(e.target as Node) &&
      popup &&
      !popup.contains(e.target as Node)
    ) {
      setOpen(false);
    }
  }, []);

  const toggleFilter = (key: FilterKey) => (e: CheckboxChangeEvent) => {
    setFilters((prev) => ({ ...prev, [key]: e.value as boolean }));
  };

  const handleTaskModeChange =
    (value: TaskMode) => (_e: RadioButtonChangeEvent) => {
      setTaskMode(value);
    };

  const anchorElement =
    anchorRef.current != null
      ? ((anchorRef.current as any).element as HTMLElement)
      : null;

  React.useEffect(() => {
    if (selectedPlayer) return;

    setTaskMode('all');
  }, [selectedPlayer]);

  React.useEffect(() => {
    if (!filters.tasks) return;

    if (selectedPlayer) {
    }
  }, [filters.tasks, taskMode, selectedPlayer]);

  React.useEffect(() => {
    if (open) document.addEventListener('mousedown', handleDocClick);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
    };
  }, [open, handleDocClick]);

  React.useEffect(() => {
    const applyFilter = (
      elements: HTMLCollectionOf<Element>,
      filter: boolean
    ) => {
      for (let i = 0; i < elements.length; i++) {
        if (filter) {
          elements[i].classList.add('d-none');
        } else {
          elements[i].classList.remove('d-none');
        }
      }
    };

    const showTasks = filters.tasks;
    const showPlayers = filters.players;
    const showOfflinePlayers = filters.offlinePlayers;

    const tasks = document.getElementsByClassName('task-container');
    applyFilter(tasks, !showTasks);

    if (taskMode === 'available') {
      for (let i = 0; i < tasks.length; i++) {
        const element = tasks[i];
        const id = element.id.replace('taskContainer-', '');
        if (unavailableTask.includes(id)) {
          element.classList.add('d-none');
        }
      }
    }

    const players = document.getElementsByClassName('player-container');
    const playerList = document.getElementsByClassName(
      'other-players-container'
    );
    applyFilter(players, !showPlayers);
    applyFilter(playerList, !showPlayers);

    const offline = document.getElementsByClassName('player-container offline');
    const offlinePlayerList = document.getElementsByClassName(
      'other-players-item offline'
    );
    applyFilter(offline, !showOfflinePlayers);
    applyFilter(offlinePlayerList, !showOfflinePlayers);
  }, [filters, taskMode, unavailableTask]);

  return (
    <div style={{ display: 'inline-block' }}>
      <Button ref={anchorRef} onClick={() => setOpen((v) => !v)}>
        <span>Filter view</span>
        <SvgIcon
          icon={open ? caretAltUpIcon : caretAltDownIcon}
          style={{ marginLeft: '8px' }}
          size="small"
        />
      </Button>

      <Popup
        anchor={anchorElement}
        anchorAlign={{
          horizontal: 'right',
          vertical: 'top'
        }}
        popupAlign={{
          horizontal: 'right',
          vertical: 'top'
        }}
        show={open}
        animate={false}
        style={{ zIndex: 1050 }}>
        <div
          ref={popupRef}
          onMouseDown={(e) => e.stopPropagation()}
          className={'facilitator-map-view-filter'}>
          <div className={'item-list'}>
            <div className={'panel-header'}>
              <span className={'panel-title'}>Filter view</span>
              <SvgIcon
                icon={caretAltDownIcon}
                size="small"
                style={{ color: '#1a3557' }}
              />
            </div>

            {FILTER_ITEMS.map(({ key, label }) => (
              <div key={key}>
                <div className={'check-row'}>
                  <Checkbox
                    checked={filters[key]}
                    onChange={toggleFilter(key)}
                    label={label}
                    size="large"
                  />
                </div>

                {key === 'tasks' &&
                  filters.tasks &&
                  selectedPlayer !== undefined && (
                    <div className={'radio-group'}>
                      {(['all', 'available'] as TaskMode[]).map((mode) => (
                        <div key={mode} className={'radio-row'}>
                          <RadioButton
                            name="taskMode"
                            value={mode}
                            checked={taskMode === mode}
                            label={
                              mode === 'all' ? 'All tasks' : 'Available tasks'
                            }
                            size="large"
                            onChange={handleTaskModeChange(mode)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default MapViewFilter;
